import { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthenticatedApi } from "./useAuthenticatedApi";

export type PolicyType =
  | "TERMS"
  | "PRIVACY"
  | "STAFF_POLICY"
  | "GUEST_POLICY"
  | "COOKIE"
  | "ANALYTICS"
  | "DATA_PROCESSING"
  | "MARKETING";

export interface UserContext {
  hotelId: string;
  employeeId: string;
  platformUserId: string;
  roleId?: string;
  role?: string;
  auth0UserId?: string;
  primaryEmail?: string;
  onboarding: {
    doneOnboarding: boolean;
    policyStatus?: {
      allRequiredAccepted: boolean;
      missingPolicies: PolicyType[];
      outdatedPolicies: PolicyType[];
    };
  };
}

let globalUserCache: UserContext | null = null;
// Track retry attempts globally to handle transient 403 errors
// Reset when authentication state changes
let retryAttempts = 0;
const MAX_RETRIES = 2;

export const useUserInfo = () => {
  const [context, setContext] = useState<UserContext | null>(globalUserCache);
  const [loading, setLoading] = useState<boolean>(!globalUserCache);
  const [error, setError] = useState<Error | null>(null);

  const { isAuthenticated } = useAuth0();
  const { callApi } = useAuthenticatedApi();
  const navigate = useNavigate();
  const location = useLocation();

  // Clear error when on error pages - this prevents the error screen from showing
  useEffect(() => {
    const isErrorPage =
      location.pathname === "/access-denied" ||
      location.pathname === "/employee-access-revoked";
    
    if (isErrorPage && error) {
      setError(null);
      setLoading(false);
    }
  }, [location.pathname, error]);

  // Clear error and context when authentication status changes
  useEffect(() => {
    // When user logs out, clear the context and retry attempts
    if (!isAuthenticated) {
      setContext(null);
      setError(null);
      globalUserCache = null;
      // Reset retry attempts on logout
      retryAttempts = 0;
      return;
    }
    // When user logs in, clear any previous errors to allow fresh fetch
    setError(null);
    // Reset retry attempts on new authentication
    retryAttempts = 0;
  }, [isAuthenticated]);

  const fetchContext = useCallback(async () => {
    // Don't fetch if we're on an error page
    const isErrorPage =
      window.location.pathname === "/access-denied" ||
      window.location.pathname === "/employee-access-revoked";
    
    if (isErrorPage) {
      setLoading(false);
      return;
    }

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await callApi({
        method: "GET",
        url: "/management/user/context",
      });
      const rawContext = data.data;

      // Backend now returns onboarding object with both profile and policy status
      const userContext: UserContext = {
        ...rawContext,
        onboarding: rawContext.onboarding || {
          doneOnboarding: false,
        },
      } as UserContext;

      globalUserCache = userContext;
      setContext(userContext);
      
      // Reset retry attempts on successful fetch
      retryAttempts = 0;
    } catch (err: unknown) {
      console.error("useUserInfo error:", err);
      const typedErr = err as { response?: { status: number } };
      
      // Check for employee access revoked error first
      const errorData = (err as any)?.response?.data;
      const isEmployeeAccessRevoked =
        typedErr?.response?.status === 403 &&
        errorData?.error?.type === "ForbiddenError" &&
        (errorData?.error?.debugInfo?.stackSummary?.includes(
          "Employee access denied"
        ) ||
          errorData?.error?.debugInfo?.stackSummary?.includes(
            "No associated employee profile found"
          ) ||
          errorData?.error?.message?.includes("do not have permission"));

      if (isEmployeeAccessRevoked) {
        // Reset retry attempts before navigating
        retryAttempts = 0;
        // Clear error state FIRST - we're handling this with navigation, not error state
        // This must happen before navigation to prevent the error screen from flashing
        setError(null);
        setLoading(false);
        // Navigate if not already on the page
        // Use React Router location check for consistency
        const currentPath = window.location.pathname;
        if (currentPath !== "/employee-access-revoked") {
          // Use replace to avoid adding to history
          navigate("/employee-access-revoked", { replace: true });
        }
        // Return early to prevent any further error handling
        return;
      }

      // Handle 403 errors with retry logic (backend might be syncing user)
      if (typedErr?.response?.status === 403) {
        if (retryAttempts < MAX_RETRIES) {
          // Increment retry count
          retryAttempts++;
          
          if (import.meta.env.DEV) {
            console.log(
              `useUserInfo: 403 error, retrying (attempt ${retryAttempts}/${MAX_RETRIES})...`
            );
          }
          
          // Exponential backoff: wait longer on each retry
          const delayMs = 1000 * retryAttempts;
          
          setTimeout(() => {
            fetchContext();
          }, delayMs);
          
          // Don't set error state yet, allow retry to complete
          setLoading(true);
          return;
        }
        
        // Retries exhausted, proceed to access denied
        if (import.meta.env.DEV) {
          console.warn("useUserInfo: 403 error persisted after retries, navigating to access-denied");
        }
      }
      
      // Don't set error state if we're on an error page
      const isErrorPage =
        window.location.pathname === "/access-denied" ||
        window.location.pathname === "/employee-access-revoked";
      
      if (!isErrorPage) {
        setError(err as Error);
      } else {
        // If we're on an error page, just clear loading and return
        setLoading(false);
        return;
      }
      
      // Navigate to access-denied for 401 or 403 (after retries exhausted)
      if (typedErr?.response && [401, 403].includes(typedErr.response.status)) {
        // Reset retry attempts before navigating
        retryAttempts = 0;
        navigate("/access-denied", { replace: true });
      }
      
      // Set loading to false (retry logic returns early, so this won't run for retries)
      setLoading(false);
    }
  }, [isAuthenticated, callApi, navigate]);

  useEffect(() => {
    // Don't fetch if we're on an error page (access-denied or employee-access-revoked)
    const isErrorPage =
      location.pathname === "/access-denied" ||
      location.pathname === "/employee-access-revoked";
    
    if (isErrorPage) {
      // Clear any error state and stop loading when on error pages
      setError(null);
      setLoading(false);
      return;
    }

    // Fetch context when authenticated
    // Force refetch if we don't have context (e.g., after logout and re-login)
    if (isAuthenticated && !context) {
      fetchContext();
    }
  }, [isAuthenticated, context, fetchContext, location.pathname]);

  const retryFetch = async () => {
    await fetchContext();
  };

  return {
    userInfo: context,
    isLoading: loading,
    error,
    retryFetch,
    hotelId: context?.hotelId,
    employeeId: context?.employeeId,
    platformUserId: context?.platformUserId,
    doneOnboarding: context?.onboarding?.doneOnboarding ?? false,
  };
};

export const clearUserCache = () => {
  globalUserCache = null;
  // Force a re-render by updating the state
  // This will trigger the useEffect to refetch
};
